import { Controller } from "@hotwired/stimulus"

// FD-1296: a toast (the flash in #flash, the suggestion filed toast) is drawn in the page.
// An open modal (the peek and the other cs-drawers, a cs-dialog, or a native <dialog>
// opened with showModal()) sits in the browser's top layer and makes the rest of the page
// inert, so a toast left in the page can be neither seen nor clicked. When a modal is open
// as the toast arrives, the toast moves into it, the way lib/suggestion_popup.js mounts its
// popup. A toast is position: fixed, and the modals only transform while they animate, so
// it still sits at the top of the window.
//
// A toast that moved replaces any earlier toast in the same modal, as a new flash replaces
// the old one in #flash. When the modal closes first, the toast goes back to where it came
// from, so it stays up for the rest of its time instead of vanishing with the modal.
//
// Moving disconnects and reconnects the toast's controllers: autohide restarts its timer,
// and this controller connects again inside the modal, which is when it starts watching
// for the modal to close (a listener set up before the move would be dropped by the
// disconnect). Where each toast came from is kept by element across that reconnect.
const homes = new WeakMap()

export default class extends Controller {
  connect() {
    const host = openModal()
    if (!host) return

    if (!host.contains(this.element)) {
      homes.set(this.element, this.element.parentElement)
      host.querySelectorAll(":scope > [data-controller~='toast-layer']").forEach((toast) => toast.remove())
      host.appendChild(this.element)
      return
    }

    const home = homes.get(this.element)
    if (!home) return
    // A cs-* modal says it has closed with cs-after-hide (it bubbles, so ignore one from
    // anything nested inside); a native <dialog> with close.
    const type = host.localName === "dialog" ? "close" : "cs-after-hide"
    const closed = (event) => {
      if (event.target === host && this.element.isConnected && home.isConnected) home.appendChild(this.element)
    }
    host.addEventListener(type, closed)
    this.stopWatching = () => host.removeEventListener(type, closed)
  }

  disconnect() {
    this.stopWatching?.()
    this.stopWatching = null
  }
}

// The modal most likely on top. A native modal <dialog> (the delete confirmation, the
// detail modal) opens over a drawer, and a cs-dialog over the page; a cs-drawer is the
// last resort. The last one in the document wins within each kind. A cs-* modal's own
// <dialog> is in its shadow root, so the element itself is the host.
function openModal() {
  const native = [...document.querySelectorAll("dialog")].filter((dialog) => dialog.matches(":modal"))
  return native.at(-1) ||
    [...document.querySelectorAll("cs-dialog[open]")].at(-1) ||
    [...document.querySelectorAll("cs-drawer[open]")].at(-1) ||
    null
}
